import type { IInjector } from '@paperbits/common/injection/IInjector';
import * as ko from 'knockout';
import type { ICmsContentPermalinkResolver } from '../../../../cms/content/ICmsContentPermalinkResolver';
import type { CmsContentContract } from '../../../../cms/content/ko/CmsContentContract';
import type { ICustomPermalinkResolver } from '../../../../media/ICustomPermalinkResolver';
import { ReplyCardModel } from '../../../reply-card/replyCardModel';
import { ReplyCardViewModel, type ReplyCardViewModelInjectables } from '../../../reply-card/ko/replyCardViewModel';
import { implictReferenceCMSContentType } from '../../constants';
import { type SERPRuntimeModel, SERPRuntimeModelBuilder } from './serpExchangeModel';
import { SERPRuntimeViewModel } from './serpRuntimeViewModel';
import type { CustomRouter } from '../../../../routing/customRouter';
import type { SearchClient, SearchQuery } from '../../../../search/client/searchClient';
import { addDelegatedEventListener } from '../../../../utils/delegate';
import { Keys } from '@paperbits/common/keyboard';
import { FilterParameters } from '../../serpUtils';
import { reducerWithBlackList } from '../../../../utils/blacklists';
import type { SelectTypeOption } from '../../../../utils/types';
import type { CategoryLabel } from '../../../../common/categories/categoryLabel';
import { CategoryLabels } from '../../../../common/categories/categoryLabels';
import type { ICMSRuntimeClient } from '../../../../cms/client/ICMSRuntimeClient';

export class SERPHTMLElement extends HTMLElement {

    private injected: ReplyCardViewModelInjectables;
    private injectionDependenciesResolved: boolean = false;
    private selectedPage: number = 1;

    private runtimeViewModel: SERPRuntimeViewModel;
    private runtimeModel: SERPRuntimeModel;
    private apiClient: ICMSRuntimeClient;
    private searchClient: SearchClient;
    private searchTimeout: ReturnType<typeof setTimeout>;
    private wrapperElements: () => NodeListOf<HTMLElement>;
    private router: CustomRouter;
    private filtersSubscriptions: any[] = [];
    private applyButtons: HTMLElement[];
    private prevFiltersValues: any[] = [];
    private dropdownsObserver: MutationObserver;

    constructor() {
        super();

        this.wrapperElements = () => this.querySelectorAll<HTMLElement>('.filters-container,.serp__results,form');
        this.applyButtonsHandler = this.applyButtonsHandler.bind(this);
        this.cardClickHandler = this.cardClickHandler.bind(this);
        this.dropdownsObserver = new MutationObserver((mutations) => {
            for (const mutation of mutations) {
                const target = <HTMLElement>mutation.target;
                if (mutation.type === 'attributes') {
                    document.body.style.overflow = target.classList.contains('show') ? 'hidden' : '';
                }
            }
        });
    }

    static get observedAttributes(): string[] {
        return ['params'];
    }

    public attributeChangedCallback(name: string, oldValue: string, newValue: string): void {
        if (name === 'params') {
            if (oldValue !== newValue && newValue?.length) {
                try {
                    this.runtimeModel = SERPRuntimeModelBuilder.fromSerialized(newValue, true);
                } catch (error) {
                    console.error('Unable to parse "params" for SERP.');
                }
                if (this.runtimeModel) {
                    this.resolveInjectionsAndApplyBindings();
                }
            }
        }
    }

    private async resolveInjectionsAndApplyBindings(): Promise<void> {
        if (!this.injectionDependenciesResolved) {
            const globalInjector: IInjector = await window['globalInjector']; // TODO(injector)
            this.router = globalInjector.resolve<CustomRouter>('router'); // TODO(injector)
            this.injected = {
                mediaPermalinkResolver: globalInjector.resolve<ICustomPermalinkResolver>('mediaPermalinkResolver'),
                cmsContentPermalinkResolver: globalInjector.resolve<ICmsContentPermalinkResolver>('cmsContentPermalinkResolver')
            };
            this.injectionDependenciesResolved = true;
        }
        this.applyBindings();
    }

    private async applyBindings(): Promise<void> {
        if (!this.runtimeViewModel) {
            this.runtimeViewModel = new SERPRuntimeViewModel();
        }
        const runtimeViewModel = this.runtimeViewModel;

        if (this.router) {
            const params: [string, string][] = this.router.getSearchParams();
            const filterParameters = new FilterParameters(params);
            if (filterParameters?.query) {
                runtimeViewModel.searchInputValue(decodeURIComponent(filterParameters.query));
            }
        }
        runtimeViewModel.searchInputValue();
        runtimeViewModel.cards([]);

        const categoriesSet: CategoryLabel[] = CategoryLabels.get();
        const categoriesOptions: SelectTypeOption[] = reducerWithBlackList(categoriesSet.map(c => ({ value: c.name, referenceId: c.key })), 'categories');
        runtimeViewModel.categoriesOptions(categoriesOptions.sort(SERPRuntimeViewModel.optionsSort));

        await this.initApiClients();
        const contentTypeSets = await this.apiClient.getContentType(implictReferenceCMSContentType);

        const industriesSet = contentTypeSets.data.metaKeys?.filter((metaKey: any) => metaKey.key === "industries")?.[0]?.optionValues || [];
        const industriesOptions: SelectTypeOption[] = reducerWithBlackList(industriesSet, 'industries');
        runtimeViewModel.industriesOptions(industriesOptions.sort(SERPRuntimeViewModel.optionsSort));

        const servicesSet = contentTypeSets.data.metaKeys?.filter((metaKey: any) => metaKey.key === "services")?.[0]?.optionValues || [];
        const servicesOptions: SelectTypeOption[] = reducerWithBlackList(servicesSet, 'services');
        runtimeViewModel.servicesOptions(servicesOptions.sort(SERPRuntimeViewModel.optionsSort));

        const fs = this.filtersSubscriptions;
        if (fs.length < 3) {
            fs.push(runtimeViewModel.categories.subscribe(this.doSearch, this));
            fs.push(runtimeViewModel.industries.subscribe(this.doSearch, this));
            fs.push(runtimeViewModel.services.subscribe(this.doSearch, this));
        }

        for (const w of this.wrapperElements()) {
            if (w !== null) {
                if (!ko.contextFor(w)) {
                    ko.applyBindings(runtimeViewModel, w);
                }
            }
        }

        for (const c of Array.from(document.querySelectorAll('.card-serp-default > a, .card-serp-default > div > a'))) {
            c.addEventListener('click', this.cardClickHandler);
        }
    }

    private cardClickHandler(e: MouseEvent): void {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            event: 'internal_search',
            eventCategory: 'site_search',
            eventAction: 'click_searched_article',
            eventLabel: `language_${this.runtimeModel.siteLanguage}`,
            resultTitle: (<HTMLElement>e.target).closest('.card-serp-default').querySelector('header, h3, h4').textContent,
            activeFilters: this.prevFiltersValues?.length > 0 ? this.prevFiltersValues.map(f => f.label).join(' - ') : 'null'
        });
    }

    private async doSearch(_options?: SelectTypeOption[], resetPageCount: boolean = true): Promise<void> {
        if (resetPageCount) { // comes from filter change
            this.selectedPage = 1;
        }

        const { activePage, cards, cardsTotal, categories, industries, searchInputValue, services } = this.runtimeViewModel;

        const query = searchInputValue();

        if (query) {
            this.router.addToSearch({ query: encodeURIComponent(query) });
            const params: SearchQuery = {
                query,
                page: this.selectedPage - 1,
                site: this.runtimeModel.siteKey,
                industries: industries().map(i => i.value),
                services: services().map(s => s.value),
                category: categories().map(c => c.value),
            };
            const res = await this.searchClient.search(params);
            const contentResults = res.hits.hits.map(h => this.searchClient.searchContentToCmsContentContract(h._source));
            cards.removeAll();
            cards.push(...await this.contentContractsToViewModels(contentResults));
            activePage(this.selectedPage);
            cardsTotal(res.hits?.total?.value);

            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
                event: 'internal_search',
                eventCategory: 'site_search',
                eventAction: 'click_submit_search',
                eventLabel: `language_${this.runtimeModel.siteLanguage}`,
                numberResults: this.runtimeViewModel.cardsTotal(),
                keyword: this.runtimeViewModel.searchInputValue()
            });
        } else {
            this.selectedPage = 1;
            cards.removeAll();
        }
    }

    public applyButtonsHandler(e: Event): void {
        const { categories, tempCategories, industries, tempIndustries, searchInputValue, services, tempServices } = this.runtimeViewModel;

        const filterType = (<HTMLElement>e.target).closest('.filter').querySelector('[data-toggle="dropdown"] > span').textContent;
        switch (filterType) {
            case 'Content type':
                categories(tempCategories());
                break;
            case 'Industry':
                industries(tempIndustries());
                break;
            case 'Service':
                services(tempServices());
                break;
            default:
                break;
        }

        const selectedFilters = [...categories(), ...industries(), ...services()].sort(SERPRuntimeViewModel.optionsSort);

        const filtersDifference = selectedFilters
            .reduce((acc: string[], f) => {
                if (!this.prevFiltersValues.includes(f)) {
                    acc.push(f.label);
                }
                return acc;
            }, [])
            .concat(this.prevFiltersValues.reduce((acc: string[], f) => {
                if (!selectedFilters.includes(f)) {
                    acc.push(f.label);
                }
                return acc;
            }, []))
            .sort();
    
        if (filtersDifference.length) {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
                event: 'internal_search',
                eventCategory: 'site_search',
                eventAction: `click_add_filter: ${filtersDifference.join('-')}`,
                eventLabel: this.prevFiltersValues?.length > 0 ? this.prevFiltersValues.map(f => f.label).join('-') : 'null',
                keyword: searchInputValue() || 'null'
            });
        }
        
        this.prevFiltersValues = selectedFilters;
    }

    public async connectedCallback(): Promise<void> {
        addDelegatedEventListener(this, 'keydown', '#serp__input', async (e: KeyboardEvent) => {
            if (e.keyCode === Keys.Enter) {
                e.preventDefault();
                clearTimeout(this.searchTimeout);
                this.searchTimeout = setTimeout(this.doSearch.bind(this), 400);
            }
        });

        addDelegatedEventListener(this, 'click', '.serp__submit', async () => {
            clearTimeout(this.searchTimeout);
            this.searchTimeout = setTimeout(this.doSearch.bind(this), 400);
        });

        addDelegatedEventListener(this, 'click', '.serp__reset', async () => {
            this.runtimeViewModel.searchInputValue('');
            this.runtimeViewModel.cards.removeAll();
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
                event: 'internal_search',
                eventCategory: 'site_search',
                eventAction: 'clear_search_textbox',
                eventLabel: `language_${this.runtimeModel.siteLanguage}`
            });
        });

        addDelegatedEventListener(this, 'click', '[data-serp-page]', async (e: MouseEvent) => {
            /// @ts-ignore/ @ts-ignore
            const pageNum = e.target.closest('[data-serp-page]').getAttribute('data-serp-page');
            this.selectedPage = Number.parseInt(pageNum);
            this.doSearch(undefined, false);
            const el = document.querySelector('.search-filters');
            if (el?.scrollIntoView) {
                el.scrollIntoView({
                    behavior: 'smooth'
                });
            }
        });

        setTimeout(() => {
            this.applyButtons = Array.from(this.querySelectorAll('[data-dismiss]'));
            for (const b of this.applyButtons) {
                b.addEventListener('mousedown', this.applyButtonsHandler, true);
            }

            for (const el of this.querySelectorAll('.filter-dropdown')) {
                this.dropdownsObserver.observe(el, { attributes: true, attributeFilter: ['class'] });
            }
        });

        addDelegatedEventListener(this, 'click', '.chip__remove', async (e: MouseEvent) => {
            const chipLabel = (<HTMLElement>e.target).closest('.chip').textContent;
            this.prevFiltersValues = this.prevFiltersValues.filter(f => f.label !== chipLabel);
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
                event: 'internal_search',
                eventCategory: 'site_search',
                eventAction: `click_remove_filter: ${chipLabel}`,
                eventLabel: this.prevFiltersValues.map(f => f.label).join('-'),
                keyword: this.runtimeViewModel.searchInputValue() || 'null'
            }); 
        });

        addDelegatedEventListener(this, 'click', '#clearAllFilters', async (_: MouseEvent) => {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
                event: 'internal_search',
                eventCategory: 'site_search',
                eventAction: 'click_clear_filter',
                eventLabel: `language_${this.runtimeModel.siteLanguage}`,
                keyword: this.runtimeViewModel.searchInputValue() || 'null'
            });
        });

        this.searchClient = (await (window['globalInjector'] as Promise<IInjector>)).resolve<SearchClient>('searchClient');

        await this.resolveInjectionsAndApplyBindings();
        this.doSearch();
    }

    public disconnectedCallback(): void {
        for (const w of this.wrapperElements()) {
            if (w !== null) {
                if (!ko.contextFor(w)) {
                    ko.cleanNode(w);
                }
            }
        }

        for (const el of this.filtersSubscriptions) {
            el.dispose();
        }
        
        for (const b of this.applyButtons) {
            b.removeEventListener('mousedown', this.applyButtonsHandler);
        }
    }

    private async contentContractsToViewModels(contracts: CmsContentContract[]): Promise<ReplyCardViewModel[]> {
        const runtimeViewModels: Promise<ReplyCardViewModel>[] = [];
        for (const contract of contracts) {
            const model = await ReplyCardModel.contentContractToModel(contract, this.injected, this.runtimeModel.siteKey, true);
            model.layout = 'card-serp-default';
            runtimeViewModels.push(ReplyCardViewModel.modelToViewModel(
                model, this.injected, null,
                { site: { key: this.runtimeModel.siteKey, language: this.runtimeModel.siteLanguage } }
            ));
        }
        return await Promise.all(runtimeViewModels);
    }

    private async initApiClients(): Promise<void> {
        if (!this.apiClient) {
            const globalInjector: IInjector = await window['globalInjector'];
            this.apiClient = globalInjector.resolve<ICMSRuntimeClient>('apiClient');
        }
    }
}